import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import WizardPreviewLayout from '../components/WizardPreviewLayout';
import WizardProgress from '../components/WizardProgress';
import WizardStepAvailability from '../components/WizardStepAvailability';
import WizardStepBasics from '../components/WizardStepBasics';
import WizardStepCheckout from '../components/WizardStepCheckout';
import WizardStepProfile from '../components/WizardStepProfile';
import WizardStepSuccess from '../components/WizardStepSuccess';
import { portalCopy } from '../copy';
import { usePartnerBusiness } from '../context/PartnerBusinessContext';
import { useWizardListingPreview } from '../hooks/useWizardListingPreview';
import type { WizardBasicsSubmit, BillingInterval } from '../types';

const PartnerWizardPage: React.FC = () => {
  const { business, createDraft, updateDraft, setWizardStep, activateBusiness } = usePartnerBusiness();
  const [showSuccess, setShowSuccess] = useState(false);

  const step = business?.status === 'expired' ? 4 : (business?.wizardStep ?? 1);
  const { preview, updatePreview } = useWizardListingPreview(business, step);

  if (business?.status === 'active' && !showSuccess) {
    return <Navigate to={`/partners/panel/${business.id}`} replace />;
  }

  const handleBasics = (data: WizardBasicsSubmit) => {
    if (business) {
      updateDraft({ ...data, wizardStep: 2 });
    } else {
      createDraft(data, 2);
    }
  };

  const handleActivate = async (billingInterval: BillingInterval) => {
    activateBusiness(billingInterval);
    setShowSuccess(true);
  };

  const renderStep = () => {
    if (showSuccess && business) {
      return <WizardStepSuccess businessId={business.id} />;
    }

    if (step === 1) {
      return <WizardStepBasics business={business} onSubmit={handleBasics} onPreviewChange={updatePreview} />;
    }

    if (step === 2 && business) {
      return (
        <WizardStepProfile
          business={business}
          onPreviewChange={updatePreview}
          onSubmit={(patch) => {
            updateDraft({ ...patch, wizardStep: 3 });
          }}
          onBack={() => setWizardStep(1)}
        />
      );
    }

    if (step === 3 && business) {
      return (
        <WizardStepAvailability
          business={business}
          onContinue={() => setWizardStep(4)}
          onBack={() => setWizardStep(2)}
          onChangeAddress={() => setWizardStep(1)}
        />
      );
    }

    if (step === 4 && business) {
      return (
        <WizardStepCheckout
          business={business}
          onActivate={handleActivate}
          onBack={() => setWizardStep(3)}
        />
      );
    }

    return <Navigate to="/partners/panel" replace />;
  };

  const showPreview = !showSuccess && step >= 1 && step <= 4;

  return (
    <div>
      <Link
        to="/partners/panel"
        className="inline-flex font-jakarta text-sm font-semibold text-chisme-primary hover:underline"
      >
        ← {portalCopy.wizard.back}
      </Link>

      <h1 className="mt-4 font-instrument text-3xl italic tracking-[-0.01em] text-[#2a0f2a]">
        {portalCopy.wizard.title}
      </h1>

      {!showSuccess && (
        <div className="mt-6">
          <WizardProgress currentStep={step} />
        </div>
      )}

      <div className="mt-8">
        <WizardPreviewLayout preview={preview} showPreview={showPreview}>
          {renderStep()}
        </WizardPreviewLayout>
      </div>
    </div>
  );
};

export default PartnerWizardPage;
